/**
 * Top-level routes. Public /login plus everything else gated by
 * <ProtectedRoute> and wrapped in <Layout>. Auth + Toast + Command
 * palette providers wrap the whole tree so any page can reach them.
 *
 * Every page is lazy-loaded (React.lazy) so the bundle splits per
 * route: a client opening a portal share link no longer downloads the
 * whole KG admin dashboard (JobDetail alone is ~4k lines), and the
 * login page paints without the app behind it. Vite emits one chunk
 * per page; the Suspense fallback is the brand loader.
 */
import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { ArollLocaleProvider } from '@/lib/aroll-i18n';
import { UploadProvider } from '@/lib/uploads';
import { ToastProvider } from '@/components/Toast';
import { ConfirmBarHost } from '@/components/ConfirmBar';
import { CommandPaletteProvider } from '@/components/CommandPalette';
import { KeyboardShortcuts } from '@/components/KeyboardShortcuts';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PortalRoute } from '@/components/PortalRoute';
import { ArollLoader } from '@/components/ArollLoader';
import { PictureUpHostGate } from '@/components/HostHandoff';
import { releasePictureUpClass } from '@/lib/pu-class';

// Pages export named components; lazy() wants a default, so each import
// maps the named export through.
const LoginPage = lazy(() => import('@/pages/Login').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })));
const SsoHandoffPage = lazy(() => import('@/pages/SsoHandoff').then((m) => ({ default: m.SsoHandoffPage })));
const PortalDashboardPage = lazy(() => import('@/pages/PortalDashboard').then((m) => ({ default: m.PortalDashboardPage })));
const PortalSharePage = lazy(() => import('@/pages/PortalDashboard').then((m) => ({ default: m.PortalSharePage })));
const DashboardPage = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const JobsPage = lazy(() => import('@/pages/JobsPage').then((m) => ({ default: m.JobsPage })));
const TimelinesPage = lazy(() => import('@/pages/Timelines').then((m) => ({ default: m.TimelinesPage })));
const LaunchBoardPage = lazy(() => import('@/pages/LaunchBoard').then((m) => ({ default: m.LaunchBoardPage })));
const CustomLaunchPage = lazy(() => import('@/pages/CustomLaunchPage').then((m) => ({ default: m.CustomLaunchPage })));
const TasksPage = lazy(() => import('@/pages/Tasks').then((m) => ({ default: m.TasksPage })));
const AccountingPage = lazy(() => import('@/pages/Accounting').then((m) => ({ default: m.AccountingPage })));
const SubscriptionsPage = lazy(() => import('@/pages/Subscriptions').then((m) => ({ default: m.SubscriptionsPage })));
const EnquiriesPage = lazy(() => import('@/pages/Enquiries').then((m) => ({ default: m.EnquiriesPage })));
const StudioDocsPage = lazy(() => import('@/pages/StudioDocs').then((m) => ({ default: m.StudioDocsPage })));
const StudioDocPage = lazy(() => import('@/pages/StudioDocs').then((m) => ({ default: m.StudioDocPage })));
const BusinessPage = lazy(() => import('@/pages/Business').then((m) => ({ default: m.BusinessPage })));
const PitchesPage = lazy(() => import('@/pages/Pitches').then((m) => ({ default: m.PitchesPage })));
const ArollSaasAdmin = lazy(() => import('@/pages/ArollSaasAdmin'));
const ArollSaasTenant = lazy(() => import('@/pages/ArollSaasTenant'));
const FolioPage = lazy(() => import('@/pages/FolioPage').then((m) => ({ default: m.FolioPage })));
const JobDetailPage = lazy(() => import('@/pages/JobDetail').then((m) => ({ default: m.JobDetailPage })));
const QuotePage = lazy(() => import('@/pages/QuotePage').then((m) => ({ default: m.QuotePage })));
const NewBriefPage = lazy(() => import('@/pages/NewBrief').then((m) => ({ default: m.NewBriefPage })));
const BriefViewPage = lazy(() => import('@/pages/BriefView').then((m) => ({ default: m.BriefViewPage })));
const FeedbackTriagePage = lazy(() => import('@/pages/FeedbackTriage').then((m) => ({ default: m.FeedbackTriagePage })));
const InboxPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.InboxPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })));
const NewJobPage = lazy(() => import('@/pages/NewJob').then((m) => ({ default: m.NewJobPage })));
const ClientsPage = lazy(() => import('@/pages/Clients').then((m) => ({ default: m.ClientsPage })));
const ClientDetailPage = lazy(() => import('@/pages/ClientDetail').then((m) => ({ default: m.ClientDetailPage })));
const NewClientPage = lazy(() => import('@/pages/NewClient').then((m) => ({ default: m.NewClientPage })));
const ContractorsPage = lazy(() => import('@/pages/Contractors').then((m) => ({ default: m.ContractorsPage })));
const ContractorDetailPage = lazy(() => import('@/pages/ContractorDetail').then((m) => ({ default: m.ContractorDetailPage })));
const NewContractorPage = lazy(() => import('@/pages/NewContractor').then((m) => ({ default: m.NewContractorPage })));


// Standalone ▲ROLL product shell — same A-Roll core, its own chrome.
// Serves both the KG dogfood tenant and A-Roll SaaS workspaces.
const ArollLoginPage = lazy(() => import('@/pages/ArollShell').then((m) => ({ default: m.ArollLoginPage })));
const ArollProjectsPage = lazy(() => import('@/pages/ArollProjects').then((m) => ({ default: m.ArollProjectsPage })));
const ArollProjectPage = lazy(() => import('@/pages/ArollShell').then((m) => ({ default: m.ArollProjectPage })));
// A-Roll SaaS signup surface (public + first-run onboarding).
const ArollSignupPage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollSignupPage })));
const ArollVerifyPage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollVerifyPage })));
const ArollForgotPasswordPage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollForgotPasswordPage })));
const ArollResetPasswordPage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollResetPasswordPage })));
const ArollEmailChangePage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollEmailChangePage })));
const ArollOnboardingPage = lazy(() => import('@/pages/ArollAuth').then((m) => ({ default: m.ArollOnboardingPage })));
const ArollSettingsPage = lazy(() => import('@/pages/ArollSettings').then((m) => ({ default: m.ArollSettingsPage })));
const ArollSharePage = lazy(() => import('@/pages/ArollSharePage').then((m) => ({ default: m.ArollSharePage })));
const ArollNotFoundPage = lazy(() => import('@/pages/ArollShell').then((m) => ({ default: m.ArollNotFoundPage })));

/** Auth gate for the ▲ROLL shell. Same rules as ProtectedRoute (team
 *  only, clients bounce to their portal) but unauthenticated users land
 *  on the ▲ROLL login, not the KG one — the shell never shows KG chrome.
 *  Lives here (not in ArollShell.tsx) so the guard doesn't statically
 *  pull the A-Roll module into the main bundle. */
/** /aroll/p/:id/playback was the review room's own address while the
 *  product had two modules; the room IS the project page now. */
function ArollLegacyPlaybackRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/aroll/p/${id ?? ''}`} replace state={{ tab: true }} />;
}

function ArollShellRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/aroll/login" replace state={{ from: location.pathname }} />;
  }
  if (user?.role === 'CLIENT') {
    return <Navigate to="/portal" replace />;
  }
  // KG staff belong in the CRM, not here (Nick, 2026-07-26). /aroll used
  // to serve BOTH audiences, costume-switching on tenantId in twelve
  // places, which is why a change could land on one view and not read
  // right on the other. Two surfaces now: the CRM for KG, /aroll for the
  // SaaS behind the landing page.
  //
  // The exact mirror of ProtectedRoute, which sends tenant users here.
  // They cannot bounce: a KG user redirected to the CRM has no tenantId
  // so ProtectedRoute passes them through, and a tenant redirected here
  // has one so this passes them through.
  if (!user?.tenantId) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function RouteFallback() {
  return (
    // The app's own preload: the rotating brand triangle, centred on the
    // page. One preloader everywhere (Nick, 2026-07-28) — this used to be
    // the animated kg gif, which is white artwork and so went invisible
    // on the light theme.
    <div className="flex min-h-screen items-center justify-center text-kg-grey-400 dark:text-white/50">
      <ArollLoader />
    </div>
  );
}

/** Undo index.html's early PictureUp switch when the app lands off
 *  PictureUp without a PictureUp page ever mounting (lib/pu-class.ts). */
function PictureUpClassGuard() {
  const { pathname } = useLocation();
  useEffect(() => {
    releasePictureUpClass(pathname);
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* A-Roll SaaS locale context. App-wide mount, A-Roll-only use:
            KG CRM pages never call useT, and English sessions load no
            extra dictionary chunk. */}
        <ArollLocaleProvider>
        {/* One upload store for the whole app, ABOVE the router: an
            upload started in a room keeps running while you walk to
            Settings, and the tray is the one place it reports from
            (Nick, 2026-07-31, option 2). */}
        <UploadProvider>
        <ToastProvider>
          <CommandPaletteProvider>
            <KeyboardShortcuts />
            <PictureUpClassGuard />
            {/* One host for every confirmInApp() call in the app — the
                dashboard, the portal and the ▲ROLL shell all sit under
                this node, so no surface has to wire it up itself. */}
            <ConfirmBarHost />
            <Suspense fallback={<RouteFallback />}>
            <PictureUpHostGate>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Standalone ▲ROLL product shell — own login, own chrome,
                  same A-Roll core as /jobs/:id. Team-only until tenant
                  plumbing lands. */}
              <Route path="/aroll/login" element={<ArollLoginPage />} />
              <Route path="/aroll/signup" element={<ArollSignupPage />} />
              {/* The road-test sandbox link is retired: signing up IS the
                  beta now (the demo project is seeded into every new
                  workspace). Any link already in the wild lands on signup
                  rather than the Kilogram CRM login, which is where the
                  catch-all would otherwise send it. */}
              <Route path="/aroll/try" element={<Navigate to="/aroll/signup" replace />} />
              <Route path="/aroll/verify/:token" element={<ArollVerifyPage />} />
              {/* PictureUp's own password-reset pair. Same endpoints as
                  /forgot-password and /reset-password below, without
                  Kilogram's Gravity chrome. */}
              <Route path="/aroll/forgot-password" element={<ArollForgotPasswordPage />} />
              <Route path="/aroll/reset-password/:token" element={<ArollResetPasswordPage />} />
              {/* Road-test entry: mints a sandbox tenant, signs the
                  visitor straight in. Public like verify/share. */}
              <Route path="/aroll/email-change/:token" element={<ArollEmailChangePage />} />
              {/* Public branded share page: must precede the guarded
                  /aroll tree so guests never bounce to the login. */}
              <Route path="/aroll/s/:token" element={<ArollSharePage />} />
              <Route
                path="/aroll/onboarding"
                element={
                  <ArollShellRoute>
                    <ArollOnboardingPage />
                  </ArollShellRoute>
                }
              />
              <Route
                path="/aroll/settings"
                element={
                  <ArollShellRoute>
                    <ArollSettingsPage />
                  </ArollShellRoute>
                }
              />
              <Route
                path="/aroll"
                element={
                  <ArollShellRoute>
                    <ArollProjectsPage />
                  </ArollShellRoute>
                }
              />
              <Route
                path="/aroll/p/:id"
                element={
                  <ArollShellRoute>
                    <ArollProjectPage />
                  </ArollShellRoute>
                }
              />
              <Route
                path="/aroll/p/:id/aroll"
                element={
                  <ArollShellRoute>
                    <ArollProjectPage tab="aroll" />
                  </ArollShellRoute>
                }
              />
              {/* The review room used to live at /playback; it is the
                  room itself now. Kept so links already shared resolve. */}
              <Route path="/aroll/p/:id/playback" element={<ArollLegacyPlaybackRedirect />} />
              {/* Anything else under /aroll is PictureUp's own not-found
                  page, not the catch-all below (that one opens the CRM). */}
              <Route path="/aroll/*" element={<ArollNotFoundPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              {/* SSO handoff target for the public-site client login.
                  The website's /login page sends us here with tokens
                  in the URL hash; we consume them and route to /portal. */}
              <Route path="/sso" element={<SsoHandoffPage />} />
              {/* Old magic-link routes — kept for backwards compat
                  so already-issued share URLs still resolve. New
                  links use the prettier /portal/<slug>/<token>
                  format below. */}
              <Route path="/portal/share/:token" element={<PortalSharePage />} />
              <Route path="/portal/share/:token/:code" element={<PortalSharePage />} />
              {/* New brandable share routes. Slug is the client name
                  (e.g. "cyclone-creative"); decorative only, the
                  token is what gates access. Two-segment form is the
                  client portal home; three-segment lands on a
                  specific job code. The existing `/portal/:code`
                  account-route is unambiguous because it has only
                  ONE segment after /portal/, while these have two
                  or three. */}
              <Route path="/portal/:slug/:token" element={<PortalSharePage />} />
              <Route path="/portal/:slug/:token/:code" element={<PortalSharePage />} />
              {/* Short share routes — the form we hand out now. No slug,
                  shortest sensible path: /p/<token> (client home) or
                  /p/<token>/<jobCode> (deep link). Same PortalSharePage;
                  the token still gates access. The longer /portal/... and
                  /portal/share/... routes above stay for older links. */}
              <Route path="/p/:token" element={<PortalSharePage />} />
              <Route path="/p/:token/:code" element={<PortalSharePage />} />
              <Route
                path="/portal"
                element={
                  <PortalRoute>
                    <PortalDashboardPage />
                  </PortalRoute>
                }
              />
              <Route
                path="/portal/:code"
                element={
                  <PortalRoute>
                    <PortalDashboardPage />
                  </PortalRoute>
                }
              />
              {/* Full-page quote document (no app chrome) so it prints
                  clean to PDF. Auth-gated but outside <Layout>. */}
              <Route
                path="/jobs/:id/quote"
                element={
                  <ProtectedRoute>
                    <QuotePage />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                {/* Cross-job to-do list (ADMIN + EDITOR; the backend
                    endpoint role-gates the data). */}
                <Route path="/tasks" element={<TasksPage />} />
                {/* Whole-team delivery overview (ADMIN + EDITOR; the
                    backend endpoint role-gates the data, and clients
                    never reach the KG app at all). */}
                <Route path="/timelines" element={<TimelinesPage />} />
                <Route path="/launch" element={<LaunchBoardPage />} />
                {/* A custom launch: not tied to one job (footage from several). */}
                <Route path="/launches/:id" element={<CustomLaunchPage />} />
                {/* In-app accounting hub — LIVE and owner-only (the
                    backend role-gates the data; the sidebar link is
                    owner-only too). Quotes + invoices are CRM-native
                    with frozen PDFs, pushed to Xero. */}
                <Route path="/accounting" element={<AccountingPage />} />
                {/* Pipeline folded into Business Manager (New work section). */}
                <Route path="/pipeline" element={<Navigate to="/business-manager" replace />} />
                <Route path="/subscriptions" element={<SubscriptionsPage />} />
                <Route path="/enquiries" element={<EnquiriesPage />} />
                <Route path="/docs" element={<StudioDocsPage />} />
                <Route path="/docs/:slug" element={<StudioDocPage />} />
                <Route path="/business-manager" element={<BusinessPage />} />
                <Route path="/pitches" element={<PitchesPage />} />
                <Route path="/pitches/:id" element={<JobDetailPage />} />
                {/* Coach and Quiet Days are views inside Business now. The
                    old addresses stay (the coach emails link to /coach). */}
                <Route path="/quiet-days" element={<Navigate to="/business-manager?tab=quiet-days" replace />} />
                <Route path="/coach" element={<Navigate to="/business-manager?tab=coach" replace />} />
                <Route path="/pictureup" element={<ArollSaasAdmin />} />
                <Route path="/pictureup/:tenantId" element={<ArollSaasTenant />} />
                {/* The back office's address before the product was
                    named. Kept so bookmarks land. */}
                <Route path="/aroll-saas" element={<Navigate to="/pictureup" replace />} />
                <Route path="/jobs/new" element={<NewJobPage />} />
                <Route path="/jobs/:id" element={<JobDetailPage />} />
                <Route path="/jobs/:id/briefs/new" element={<NewBriefPage />} />
                <Route path="/briefs/:id" element={<BriefViewPage />} />
                <Route path="/jobs/:id/feedback" element={<FeedbackTriagePage />} />
                <Route path="/folio" element={<FolioPage />} />
                {/* Folded into /folio as the Home/About/Contact tabs.
                    Redirect rather than 404 so old bookmarks still land. */}
                <Route path="/site" element={<Navigate to="/folio" replace />} />
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/new" element={<NewClientPage />} />
                <Route path="/clients/:id" element={<ClientDetailPage />} />
                <Route path="/contractors" element={<ContractorsPage />} />
                <Route path="/contractors/new" element={<NewContractorPage />} />
                <Route path="/contractors/:id" element={<ContractorDetailPage />} />
                <Route path="/inbox" element={<InboxPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </PictureUpHostGate>
            </Suspense>
          </CommandPaletteProvider>
        </ToastProvider>
        </UploadProvider>
        </ArollLocaleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
